import { Pagination, Rsp } from "@repo/types";
import { getHttpClient } from "./http";
import { UserAPI } from "./user";
import type {
  BatchCheckResultBody,
  BlacklistCheckResult,
  BlacklistItem,
  BlacklistPublicSettings,
  CheckResult,
  MyBlacklistEntry,
  MyBlacklistUser,
  SharedSetEntry,
  SharedSetUserEntry,
  SubscriptionEntry,
} from "./blacklist.type";

export const BlacklistServiceAPI = {
  getMyBlacklist(page = 1, pageSize = 10) {
    return getHttpClient().get<Rsp<Pagination<MyBlacklistEntry[]>>>({
      url: `/psd/blacklist/my?page=${page}&page_size=${pageSize}`,
    });
  },

  getSharedBlacklistSet(setId: number) {
    return getHttpClient().get<Rsp<SharedSetEntry>>({ url: `/psd/blacklist/shared/${setId}` });
  },

  getSharedBlacklists(page = 1, pageSize = 10, ownerUid?: string) {
    return getHttpClient().get<Rsp<Pagination<SharedSetEntry[]>>>({
      url:
        `/psd/blacklist/shared?page=${page}&page_size=${pageSize}` +
        (ownerUid ? `&owner_uid=${ownerUid}` : ""),
    });
  },

  getSharedBlacklistUsers(
    setId: number,
    page = 1,
    pageSize = 10,
    filters?: { uid?: string; reason?: string; onlyWithEvidence?: boolean },
  ) {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (filters?.uid) params.set("uid", filters.uid);
    if (filters?.reason) params.set("reason", filters.reason);
    if (filters?.onlyWithEvidence) params.set("only_with_evidence", "true");
    return getHttpClient().get<Rsp<Pagination<SharedSetUserEntry[]>>>({
      url: `/psd/blacklist/shared/${setId}/users?${params.toString()}`,
    });
  },

  addToBlacklist(blockedUid: string, options?: { reason?: string; images?: string[] }) {
    return getHttpClient().post<Rsp<null>>({
      url: "/psd/blacklist/add",
      data: {
        blocked_uid: blockedUid,
        reason: options?.reason,
        ...(options?.images?.length ? { images: options.images } : {}),
      },
    });
  },

  removeFromBlacklist(blockedUid: string) {
    return getHttpClient().post<Rsp<null>>({
      url: "/psd/blacklist/remove",
      data: { blocked_uid: blockedUid },
    });
  },

  updateBlacklistEntry(blockedUid: string, options: { reason?: string; images: string[] }) {
    return getHttpClient().put<Rsp<null>>({
      url: "/psd/blacklist/update",
      data: { blocked_uid: blockedUid, reason: options.reason, images: options.images },
    });
  },

  getBlacklistPublicSettings() {
    return getHttpClient().get<Rsp<BlacklistPublicSettings>>({ url: "/psd/blacklist/public" });
  },

  setBlacklistPublic(isPublic: boolean) {
    return getHttpClient().put<Rsp<null>>({ url: "/psd/blacklist/public", data: { is_public: isPublic } });
  },

  updateBlacklistSettings(params: { title: string; is_public: boolean }) {
    return getHttpClient().put<Rsp<null>>({
      url: "/psd/blacklist/public",
      data: { title: params.title, is_public: params.is_public },
    });
  },

  getSubscriptions(page = 1, pageSize = 10) {
    return getHttpClient().get<Rsp<Pagination<SubscriptionEntry[]>>>({
      url: `/psd/blacklist/subscriptions?page=${page}&page_size=${pageSize}`,
    });
  },

  checkUid(targetUid: string) {
    return getHttpClient().post<Rsp<CheckResult>>({
      url: "/psd/blacklist/check",
      data: { target_uid: targetUid },
    });
  },

  checkUidBatch(targetUids: string[]) {
    return getHttpClient().post<Rsp<BatchCheckResultBody>>({
      url: "/psd/blacklist/check/batch",
      data: { target_uids: targetUids },
    });
  },

  subscribe(setId: number) {
    return getHttpClient().post<Rsp<null>>({ url: "/psd/blacklist/subscribe", data: { set_id: setId } });
  },

  unsubscribe(setId: number) {
    return getHttpClient().post<Rsp<null>>({ url: "/psd/blacklist/unsubscribe", data: { set_id: setId } });
  },
};

// ── 应用层封装（原 apps/web blacklist&query/api）────────────────────────────

async function queryUserBriefMap(uids: string[]) {
  const uniqueUids = Array.from(new Set(uids.filter(Boolean)));
  if (uniqueUids.length === 0) return new Map<string, { avatar_url: string; nickname: string }>();

  try {
    const { data } = await UserAPI.batchQueryByUIDs(uniqueUids);
    return new Map(
      data.hit.map((item) => [
        item.uid,
        { avatar_url: item.avatar_url, nickname: item.nickname || item.uid },
      ]),
    );
  } catch {
    return new Map<string, { avatar_url: string; nickname: string }>();
  }
}

function parseSetId(id: string): number {
  const parsed = Number(id);
  if (!Number.isFinite(parsed)) {
    throw new Error("无效的黑名单 ID");
  }
  return parsed;
}

/** 黑名单业务 API（列表映射、批量补全用户资料等） */
export const BlacklistAPI = {
  async getSubscribedBlacklists(): Promise<BlacklistItem[]> {
    const { data } = await BlacklistServiceAPI.getSubscriptions(1, 20);
    return data.content.map((item) => ({
      id: String(item.set_id),
      name: `${item.title}`,
      uid: item.uid,
      nickname: item.nickname,
      avatar_url: item.avatar_url,
      user_count: item.user_count,
      updated_at: item.subscribed_at,
      available: item.available,
      is_subscribed: true,
    }));
  },

  async getBlacklistItem(id: string): Promise<BlacklistItem> {
    const setId = parseSetId(id);
    const { data } = await BlacklistServiceAPI.getSharedBlacklistSet(setId);
    return {
      id: String(data.set_id),
      name: data.title,
      uid: data.uid,
      nickname: data.nickname,
      avatar_url: data.avatar_url,
      user_count: data.user_count,
      updated_at: data.created_at,
      is_subscribed: false,
      available: data.available,
    };
  },

  async getPublicBlacklists(
    page = 1,
    pageSize = 10,
  ): Promise<{ list: BlacklistItem[]; total: number; totalPages: number }> {
    const [sharedRes, subscribedRes] = await Promise.all([
      BlacklistServiceAPI.getSharedBlacklists(page, pageSize),
      BlacklistServiceAPI.getSubscriptions(1, 20),
    ]);

    const subscribedSet = new Set(subscribedRes.data.content.map((item) => item.set_id));
    return {
      list: sharedRes.data.content.map((item) => ({
        id: String(item.set_id),
        name: `${item.title}`,
        uid: item.uid,
        nickname: item.nickname,
        avatar_url: item.avatar_url,
        user_count: item.user_count,
        updated_at: item.created_at,
        is_subscribed: subscribedSet.has(item.set_id),
        available: item.available,
      })),
      total: sharedRes.data.total,
      totalPages: sharedRes.data.total_pages,
    };
  },

  async getMyBlacklist(
    page = 1,
    pageSize = 10,
  ): Promise<{ list: MyBlacklistUser[]; total: number; totalPages: number }> {
    const { data } = await BlacklistServiceAPI.getMyBlacklist(page, pageSize);
    const userMap = await queryUserBriefMap(data.content.map((item) => item.blocked_uid));
    return {
      list: data.content.map((item) => ({
        uid: item.blocked_uid,
        avatar_url: userMap.get(item.blocked_uid)?.avatar_url || "",
        nickname: userMap.get(item.blocked_uid)?.nickname || item.blocked_uid,
        added_at: item.created_at,
        reason: item.reason,
        evidence: item.evidence,
      })),
      total: data.total,
      totalPages: data.total_pages,
    };
  },

  async getBlacklistUsers(
    blacklistId: string,
    page = 1,
    pageSize = 20,
    filters?: { uid?: string; reason?: string; onlyWithEvidence?: boolean },
  ): Promise<{ list: MyBlacklistUser[]; total: number; totalPages: number }> {
    const setId = parseSetId(blacklistId);
    const { data } = await BlacklistServiceAPI.getSharedBlacklistUsers(setId, page, pageSize, filters);
    const userMap = await queryUserBriefMap(data.content.map((item) => item.blocked_uid));
    return {
      list: data.content.map((item) => ({
        uid: item.blocked_uid,
        avatar_url: userMap.get(item.blocked_uid)?.avatar_url || "",
        nickname: userMap.get(item.blocked_uid)?.nickname || item.blocked_uid,
        added_at: item.created_at,
        reason: item.reason,
        evidence: item.evidence,
      })),
      total: data.total,
      totalPages: data.total_pages,
    };
  },

  async subscribeBlacklist(id: string): Promise<void> {
    await BlacklistServiceAPI.subscribe(parseSetId(id));
  },

  async unsubscribeBlacklist(id: string): Promise<void> {
    await BlacklistServiceAPI.unsubscribe(parseSetId(id));
  },

  async addToBlacklist(
    uid: string,
    userInfo?: {
      avatar_url?: string;
      nickname?: string;
      reason?: string;
      images?: string[];
    },
  ): Promise<void> {
    await BlacklistServiceAPI.addToBlacklist(uid, {
      reason: userInfo?.reason,
      images: userInfo?.images,
    });
  },

  async removeFromBlacklist(uid: string): Promise<void> {
    await BlacklistServiceAPI.removeFromBlacklist(uid);
  },

  async updateBlacklistEntry(uid: string, params: { reason?: string; images: string[] }): Promise<void> {
    await BlacklistServiceAPI.updateBlacklistEntry(uid, params);
  },

  async shareBlacklist(): Promise<void> {
    await BlacklistServiceAPI.setBlacklistPublic(true);
  },

  async getBlacklistPublicSettings(): Promise<{ title: string; is_public: boolean }> {
    const { data } = await BlacklistServiceAPI.getBlacklistPublicSettings();
    return { title: data.title ?? "", is_public: data.is_public ?? false };
  },

  async updateBlacklistSettings(params: { title: string; is_public: boolean }): Promise<void> {
    await BlacklistServiceAPI.updateBlacklistSettings(params);
  },

  async checkUid(uid: string): Promise<BlacklistCheckResult> {
    const { data } = await BlacklistServiceAPI.checkUid(uid);
    return {
      hit: data.hit,
      source: data.source.map((item) => ({
        name: item.name,
        id: Number(item.id),
      })),
    };
  },
};
